import "antd/dist/reset.css";
import { Layout as Container } from "antd";
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from "react-router-dom";
import { lazy } from "react";
import { Suspense } from "react"; 

const Auth                 = lazy(() => import("./router/Auth"));
const RegisterPage         = lazy(() => import("./router/RegisterPage"));
const Home                 = lazy(() => import("./router/Home"));
const Layout               = lazy(() => import("./router/Layout"));
const NotFound             = lazy(() => import("./router/NotFound"));
const Settings             = lazy(() => import("./router/Settings"));
const Strategy             = lazy(() => import("./router/Strategy"));
const Analytics            = lazy(() => import("./router/Analytics"));
const Integration          = lazy(() => import("./router/Integration"));
const ProjectsPage         = lazy(() => import("./router/ProjectsPage"));
const ImportHistory        = lazy(() => import("./router/ImportHistory"));
const LeadGeneration       = lazy(() => import("./router/LeadGeneration"));
const IndividualSearch     = lazy(() => import("./router/IndividualSearch"));
const SearchResultsPage    = lazy(() => import("./router/SearchResultTable"));
const SmartSalesAgent      = lazy(() => import("./router/SmartSalesAgent"));
const Help                 = lazy(() => import("./router/Help"));
const ProximitySearch      = lazy(() => import("./router/ProximitySearch"));
const LeadGenerationByAi   = lazy(() => import("./router/LeadGenerationByAi"));
const Tasks                = lazy(() => import("./router/Tasks"));
const AivAnalytics         = lazy(() => import("./router/AivAnalytics"));
const AivAnalyticsQS       = lazy(() => import("./router/AivAnalyticsQS"));
const ProjectsGraphics     = lazy(() => import("./router/ProjectsGraphics.tsx"));
const Credits              = lazy(() => import("./router/Credits"));
const SmartSalesAgentHome  = lazy(() => import("./router/SmartSalesAgentHome.tsx"));
const AivSettings          = lazy(() => import("./components/SmartSalesAgent/Settings.tsx"));
const AivTeams             = lazy(() => import("./router/AivTeams.tsx"))
const AivChat              = lazy(() =>import("./router/AivChat.tsx"))
const AivCrm               = lazy(() => import("./router/AivCrm.tsx"))
const TesteMeta            = lazy(() => import("./router/TesteMeta.tsx"));
const ModelsPage = lazy(() => import("./router/ModelsPage.tsx"));

import { useAuth } from "./hooks/useAuth.tsx";
import { Spin } from "antd";
import { CrmProvider } from "./components/CrmDropdown.tsx";
import { AdsProvider } from "./components/AdsDropdown.tsx";
import { AivProvider } from "./components/aivListDropdown.tsx";
import { GenerateLeadsAI } from "./router/generateLeadsAi.tsx";



type CustomAttributes = {
  ajuda: string;
  busca_marcas: string;
  configuracoes: string;
  processos: string;
  prova_anterioridade: string;
};

function DynamicRoute({
  attributeKey,
  fallbackPath,
  component: Component,
}: {
  attributeKey: keyof CustomAttributes;
  fallbackPath: string;
  component: React.ComponentType;
}) {
  const { customAttributes } = useAuth();
  const isAuthorized = customAttributes[attributeKey] === "1";

  return isAuthorized ? <Component /> : <Navigate to={fallbackPath} />;
}

function RequireAuth({ children }: { children?: React.ReactNode }) {
  const auth  = useAuth();

  if (!auth.isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  return <>{children ?? <Outlet />}</>;
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <Auth />,
  },
  {
    path: "/register",
    element: <RegisterPage />,
  },
  {
    path: "/meta",
    element: <TesteMeta />,
  },
  {
    path: "/painel",
    element: <RequireAuth><Layout /></RequireAuth>,
    children: [
      {
        path: "inicio",
        element: <Home />,
      },
      {
        path: "vendas",
        element: <Analytics />,
      },
      {
        path: "strategy",
        element: <Strategy />,
      },
      {
        path: "integration",
        element: (
        <CrmProvider>
          <AdsProvider>
            <AivProvider>
              <Integration />
            </AivProvider>
          </AdsProvider>
        </CrmProvider>),
      },
      {
        path: "busca-individual",
        element: (
        <CrmProvider>
          <AdsProvider>
          <AivProvider>  
            <IndividualSearch />
          </AivProvider>
          </AdsProvider>
          </CrmProvider>),
      },
      {
        path: "geracao-leads",
        element: <LeadGeneration />,
      },
      {
        path: "geracao-por-ia",
        element: <GenerateLeadsAI />,
      },
      {
        path: "geracao-leads-ai",
        element: <LeadGenerationByAi />,
      },
      {
        path: "busca-por-semelhanca",
        element: <ProximitySearch />,
      },
      {
        path: "leads-results",
        element: (
        <CrmProvider>
          <AdsProvider>
            <AivProvider>  
              <SearchResultsPage />
            </AivProvider>
          </AdsProvider>
        </CrmProvider>),
      },
      {
        path: "historico-de-importacoes",
        element: 
        <CrmProvider>
          <AdsProvider>
            <AivProvider>
              <ImportHistory />
            </AivProvider>          
          </AdsProvider>
        </CrmProvider>,
      },
      {
        path: "agente-inteligente-de-vendas-home",
        element: <SmartSalesAgentHome/>
      },
      {
        path: "agente-inteligente-de-vendas",
        element: <SmartSalesAgent/>
      },
      {
        path:"agente-inteligente-de-vendas-configuracoes/:agentType",
        element: <AivSettings/>
      },
      {
        path: "tarefas",
        element: <Tasks/>
      },
      {
        path: "aiv-chat",
        element: <AivChat key='chat'/>
      },
      {
        path: 'aiv-free-chat',
        element: <AivChat free key='freeChat'/>
      },
      {
        path: "aiv-crm",
        element: <AivCrm/>
      },
      {
        path: "aiv-equipes",
        element: <AivTeams/>
      },
      {
        path: "analytics_qs",
        element: <AivAnalyticsQS/>
      },
      {
        path: "aiv_analytics",
        element: <AivAnalytics/>
      },
      {
        path: "administrativo",
        element: <Settings />
      },
      {
        path: "modelos",
        element: <ModelsPage />
      },
      {
        path: ":page/:domain",
        element: <ProjectsPage />,
      },
      {
        path: "grafico/:domain/:fileKey",
        element: <ProjectsGraphics />,
      },
      {
        path: "ajuda",
        element: <Help />,
      },
      {
        path: "creditos",
        element: <Credits/>
      },
      {
        path: "*",
        element: <NotFound />,
      }
    ],
  },
]);

export default function App() {
  const { loading } = useAuth();
  if (loading) return <Spin size="large" style={{width: '100%', marginTop: '50vh'}}/>;
  return (
    <Container style={{ minHeight: "100vh" }}>
        <Suspense fallback={<Spin size="large" style={{ width: '100%', marginTop: '50vh' }} />}>
          <RouterProvider router={router} fallbackElement={<Fallback />} />
        </Suspense>
    </Container>
  );
}

export function Fallback() {
  return <p>Performing initial data load</p>;
}
